import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SendIcon from "@mui/icons-material/Send";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import RefreshIcon from "@mui/icons-material/Refresh";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useSnackbar } from "context";
import { financeText } from "finance-language";

import { FinanceStatusChip } from "../shared/FinanceUiKit";
import { bankRowsForInvoice, isForeignCountry, resolveCustomerCountry } from "../shared/bankAccounts";

const API_URL = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// Currencies offered by the "Display Currency" selector. It only affects the
// three roll-up summary cards at the top of the page; the returned-item rows,
// the document's own totals block and the exported PDF always stay in the
// return invoice's native currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const resolvePdfUrl = (value) => {
    if (!value) return "";

    const pdfPath = String(value).trim();
    if (!pdfPath) return "";
    if (/^(https?:|blob:|data:)/i.test(pdfPath)) return pdfPath;

    const apiOrigin = API_URL.replace(/\/api$/i, "");
    return `${apiOrigin}${pdfPath.startsWith("/") ? "" : "/"}${pdfPath}`;
};

const unwrapData = (payload) =>
    payload?.data?.invoice ||
    payload?.data?.data ||
    payload?.invoice ||
    payload?.data ||
    payload ||
    null;

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const firstValue = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== "");

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "—"
        : new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(date);
};

const formatMoney = (value, currency = "EUR") => {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "EUR").toUpperCase(),
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(toNumber(value));
    } catch {
        return `${toNumber(value).toFixed(2)} ${currency || "EUR"}`;
    }
};

const normalizeItems = (invoice) => {
    const safeInvoice =
        invoice && typeof invoice === "object" ? invoice : {};

    // A return document must never inherit all original invoice items.
    // Prefer the return-specific item arrays and fall back only to the items
    // stored on the return invoice record itself.
    const source = [
        safeInvoice.returnItems,
        safeInvoice.returnedItems,
        safeInvoice.returnInvoiceItems,
        safeInvoice.invoiceItems,
        safeInvoice.items,
    ].find(Array.isArray) || [];

    return source.map((item, index) => {
        const quantity = toNumber(firstValue(item.quantity, item.unitQuantity, 1));
        const unitPrice = toNumber(firstValue(item.unitPrice, item.sellingRate, 0));
        const amount = toNumber(firstValue(item.amount, quantity * unitPrice));
        const vatRate = toNumber(firstValue(item.vatRate, 0));
        const vatAmount = toNumber(firstValue(item.vatAmount, amount * (vatRate / 100)));

        return {
            id: firstValue(item._id, item.id, item.originalInvoiceItemId, index),
            code: firstValue(item.code, item.itemCode, item.invoiceItem, "RETURN"),
            description: firstValue(
                item.description,
                item.commodity,
                item.label,
                "Returned invoice item",
            ),
            quantity,
            unitPrice,
            amount,
            vatRate,
            vatAmount,
            totalWithVat: toNumber(firstValue(item.totalWithVat, amount + vatAmount)),
        };
    });
};

const loadPdfImageAsBase64 = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            canvas.getContext("2d").drawImage(image, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        image.onerror = () => reject(new Error("Invoice letterhead could not be loaded."));
        image.src = url;
    });

const createReturnInvoicePdf = async (invoice) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pdfItems = normalizeItems(invoice);
    const pdfCurrency = firstValue(invoice?.currency, invoice?.invoiceCurrency, "EUR");
    // Same VAT-inclusive/exclusive distinction as the on-screen totals above
    // (invoice.totalAmount is the grand total, not the subtotal).
    const pdfSubtotal = toNumber(
        firstValue(
            invoice?.invoiceSalesAmount,
            pdfItems.reduce((sum, item) => sum + item.amount, 0),
        ),
    );
    const pdfVatTotal = toNumber(
        firstValue(
            invoice?.vatAmount,
            pdfItems.reduce((sum, item) => sum + item.vatAmount, 0),
        ),
    );
    const pdfGrandTotal = toNumber(
        firstValue(invoice?.totalAmount, pdfSubtotal + pdfVatTotal),
    );

    const cleanText = (value) =>
        String(firstValue(value, "N/A"))
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[ğĞ]/g, "g")
            .replace(/[şŞ]/g, "s")
            .replace(/[ıİ]/g, "i")
            .replace(/[çÇ]/g, "c")
            .replace(/[öÖ]/g, "o")
            .replace(/[üÜ]/g, "u");
    const PAGE_TOP = 45;
    let letterheadImage = null;

    try {
        letterheadImage = await loadPdfImageAsBase64("/images/letterhead.png");
    } catch (letterheadError) {
        console.warn(letterheadError.message);
    }

    const drawLetterhead = () => {
        if (letterheadImage) {
            doc.addImage(letterheadImage, "PNG", 0, 0, pageWidth, pageHeight);
        }
    };
    const tableOptions = {
        theme: "grid",
        styles: { lineWidth: 0.7, fontSize: 10 },
        headStyles: { fillColor: [16, 28, 54], textColor: 255 },
    };

    drawLetterhead();
    let y = 35;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(
        String(invoice?.returnStatus || "Draft") === "Draft"
            ? "DRAFT RETURN INVOICE"
            : "RETURN INVOICE",
        pageWidth / 2,
        y,
        { align: "center" },
    );
    doc.setLineWidth(0.7);
    doc.line(50, y + 7, 160, y + 7);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Date: ${formatDate(invoice?.invoiceDate || invoice?.createdAt)}`, pageWidth - 20, y, {
        align: "right",
    });

    y += 15;
    doc.setFont("helvetica", "bold");
    doc.text("Sender Information", pageWidth / 2, y, { align: "center" });
    autoTable(doc, {
        startY: y + 5,
        margin: { top: PAGE_TOP },
        head: [["Field", "Details", "Field", "Details"]],
        body: [
            ["Company", "Filenders Lojistik ve Teknoloji A.S", "Phone", "+90 324 503 84 33"],
            ["Email", "accounting@filenders.com", "Payment Notice", "control.finance@filenders.com"],
            [
                "Address",
                "Mersin University Ciftlikkoy Campus Technopark, 1st Incubation Building 35B/B02, Yenisehir/Mersin",
                "TAX NO",
                "3871329803",
            ],
        ],
        ...tableOptions,
    });

    y = (doc.lastAutoTable?.finalY || y) + 10;
    doc.setFont("helvetica", "bold");
    doc.text("Customer Information", pageWidth / 2, y, { align: "center" });
    autoTable(doc, {
        startY: y + 5,
        margin: { top: PAGE_TOP },
        head: [["Field", "Details", "Field", "Details"]],
        body: [
            [
                "Return Invoice No",
                cleanText(firstValue(invoice?.returnInvoiceNumber, invoice?.invoiceNumber, invoice?.invoiceNo)),
                "Return Date",
                cleanText(formatDate(invoice?.invoiceDate || invoice?.createdAt)),
            ],
            [
                "Customer",
                cleanText(firstValue(invoice?.customerName, invoice?.customer?.companyName, invoice?.companyName)),
                "Email",
                cleanText(firstValue(invoice?.email, invoice?.customer?.email)),
            ],
            [
                "Tax ID",
                cleanText(firstValue(invoice?.taxID, invoice?.taxId, invoice?.customer?.taxID)),
                "Invoice Address",
                cleanText(firstValue(invoice?.billingAddress, invoice?.customer?.billingAddress, invoice?.customer?.address)),
            ],
        ],
        ...tableOptions,
    });

    autoTable(doc, {
        startY: (doc.lastAutoTable?.finalY || 100) + 10,
        margin: { top: PAGE_TOP },
        head: [["#", "Invoice Item", "Description", "Qty", "Unit Price", "VAT", "Amount"]],
        body: pdfItems.map((item, index) => [
            index + 1,
            cleanText(item.code),
            cleanText(item.description),
            item.quantity,
            formatMoney(item.unitPrice, pdfCurrency),
            `${item.vatRate.toFixed(2)}%`,
            formatMoney(item.totalWithVat, pdfCurrency),
        ]),
        ...tableOptions,
        styles: { ...tableOptions.styles, halign: "center", fontSize: 9 },
    });

    const totalsY = (doc.lastAutoTable?.finalY || 140) + 10;
    doc.setFontSize(10);
    doc.text(`Subtotal: ${formatMoney(pdfSubtotal, pdfCurrency)}`, pageWidth - 20, totalsY, {
        align: "right",
    });
    doc.text(`VAT: ${formatMoney(pdfVatTotal, pdfCurrency)}`, pageWidth - 20, totalsY + 6, {
        align: "right",
    });
    doc.setFont("helvetica", "bold");
    doc.text(`Return Total: ${formatMoney(pdfGrandTotal, pdfCurrency)}`, pageWidth - 20, totalsY + 12, {
        align: "right",
    });

    if (invoice?.returnReason) {
        doc.setFont("helvetica", "normal");
        doc.text("Return Reason:", 14, totalsY + 24);
        doc.text(doc.splitTextToSize(String(invoice.returnReason), 180), 14, totalsY + 30);
    }

    doc.addPage();
    drawLetterhead();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("BANK DETAILS", pageWidth / 2, PAGE_TOP - 10, { align: "center" });
    autoTable(doc, {
        startY: PAGE_TOP,
        margin: { top: PAGE_TOP },
        head: [["BANK", "CURRENCY", "IBAN", "BRANCH", "SWIFT"]],
        body: bankRowsForInvoice([
            ["T.GARANTI BANKASI", "USD", "TR20 0006 2001 1730 0009 0929 99", "MERSIN SANAYI", "TGBATRISXXX"],
            ["T.GARANTI BANKASI", "EURO", "TR90 0006 2001 1730 0009 0930 00", "MERSIN SANAYI", "TGBATRISXXX"],
            ["T.GARANTI BANKASI", "GBP", "TR20 0006 2001 1730 0009 0915 44", "MERSIN SANAYI", "TGBATRISXXX"],
            ["T.GARANTI BANKASI", "TRY", "TR94 0006 2001 1730 0006 2962 24", "MERSIN SANAYI", ""],
            ["T.IS BANKASI MERSIN", "USD", "TR34 0006 4000 0026 6030 1533 33", "KUCUK SANAYI", "ISBKTRIS"],
            ["T.IS BANKASI MERSIN", "EURO", "TR72 0006 4000 0026 6030 1533 28", "KUCUK SANAYI", "ISBKTRIS"],
            ["T.IS BANKASI MERSIN", "TRY", "TR98 0006 4000 0016 6030 3901 27", "KUCUK SANAYI", ""],
            ["ZIRAAT BANKASI MERSIN", "USD", "TR80 0001 0019 6097 8746 9350 02", "INONU BULVARI", "TCZBTR2A"],
            ["ZIRAAT BANKASI MERSIN", "EURO", "TR53 0001 0019 6097 8746 9350 03", "INONU BULVARI", "TCZBTR2A"],
            ["ZIRAAT BANKASI MERSIN", "TRY", "TR10 0001 0019 6097 8746 9350 01", "INONU BULVARI", ""],
            ["VAKIFBANK", "USD", "TR72 0001 5001 5804 8024 3766 06", "POZCU MERSIN", ""],
            ["VAKIFBANK", "EURO", "TR62 0001 5001 5804 8024 3766 89", "POZCU MERSIN", ""],
            ["VAKIFBANK", "TRY", "TR05 0001 5001 5800 7333 9760 46", "POZCU MERSIN", ""],
        ], { currency: pdfCurrency, foreign: isForeignCountry(await resolveCustomerCountry(invoice)) }),
        ...tableOptions,
        styles: { ...tableOptions.styles, fontSize: 9 },
    });

    const paymentBodyText = `Payments must be made in full within the payment term specified on this invoice.\nThe due date is calculated as of the invoice date and is explicitly stated on the invoice.\n\nUpon completion of payment, the SWIFT transfer confirmation must be sent to:\n• control.finance@filenders.com\n• accounting@filenders.com`;
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        theme: "grid",
        head: [["Payment Terms and Notification"]],
        body: [[paymentBodyText]],
        styles: { fontSize: 9, lineWidth: 0.4, cellPadding: 7 },
        headStyles: { fillColor: [16, 28, 54], textColor: 255, halign: "center" },
    });

    return doc;
};

const DetailField = ({ label, value }) => (
    <Box>
        <MDTypography variant="caption" color="text" fontWeight="medium">
            {label}
        </MDTypography>
        <MDTypography variant="button" fontWeight="medium" display="block" mt={0.35}>
            {value || "—"}
        </MDTypography>
    </Box>
);

const SummaryCard = ({ label, value, color = "#111827", background = "#f8fafc" }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            height: "100%",
            border: "1px solid #e5e7eb",
            borderRadius: 2.5,
            background,
        }}
    >
        <MDTypography variant="caption" color="text" fontWeight="medium">
            {label}
        </MDTypography>
        <MDTypography variant="h5" mt={0.5} sx={{ color }}>
            {value}
        </MDTypography>
    </Paper>
);

function ReturnInvoiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [sendingEdm, setSendingEdm] = useState(false);
    const [checkingGibStatus, setCheckingGibStatus] = useState(false);
    const [exchangeRates, setExchangeRates] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (rateError) {
                console.error("Exchange rate fetch error:", rateError);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    // Returns the raw amount untouched when both currencies match, and null
    // when a rate is missing, so callers can fall back to the native figure
    // instead of silently rendering a wrong number.
    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = String(fromCurrency || "").toUpperCase();
            const to = String(toCurrency || "").toUpperCase();

            if (!from || !to || from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const fetchWithAuth = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: {
                ...(options.body ? { "Content-Type": "application/json" } : {}),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...options.headers,
            },
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload?.message || payload?.error || financeText("Request failed."));
        }
        return payload;
    }, []);

    const loadInvoice = useCallback(async () => {
        if (!id) {
            setError(financeText("Return invoice ID is missing."));
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const payload = await fetchWithAuth(`/finance/return-invoices/${id}`);
            const record = unwrapData(payload);

            if (!record || typeof record !== "object") {
                throw new Error(financeText("Return invoice data is invalid."));
            }

            setInvoice(record);
        } catch (requestError) {
            setError(requestError.message || financeText("Return invoice could not be loaded."));
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth, id]);

    useEffect(() => {
        loadInvoice();
    }, [loadInvoice]);

    const uploadReturnInvoicePdf = useCallback(async (currentInvoice) => {
        if (!currentInvoice?._id) {
            throw new Error(financeText("Return invoice data is missing."));
        }

        const doc = await createReturnInvoicePdf(currentInvoice);
        const pdfBuffer = doc.output("arraybuffer");
        const invoiceNumber = firstValue(
            currentInvoice.returnInvoiceNumber,
            currentInvoice.invoiceNumber,
            currentInvoice._id,
        );
        const safeInvoiceNumber = String(invoiceNumber).replace(/[^a-zA-Z0-9_-]/g, "_");
        const pdfFile = new File(
            [pdfBuffer],
            `return-invoice-${safeInvoiceNumber}.pdf`,
            { type: "application/pdf" },
        );

        const formData = new FormData();
        formData.append("file", pdfFile);
        formData.append(
            "customerName",
            firstValue(
                currentInvoice.customerName,
                currentInvoice.customer?.companyName,
                currentInvoice.companyName,
                "Customer",
            ),
        );
        formData.append("email", firstValue(currentInvoice.email, currentInvoice.customer?.email, ""));
        formData.append("returnInvoiceId", currentInvoice._id);

        const shipmentId = firstValue(
            currentInvoice.shipmentId?._id,
            currentInvoice.shipmentId,
        );
        if (shipmentId) formData.append("shipmentId", shipmentId);

        const originalId = firstValue(
            currentInvoice.originalInvoiceId?._id,
            currentInvoice.originalInvoiceId,
        );
        if (originalId) formData.append("invoiceId", originalId);

        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/email/send-invoice-mail`, {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(
                result?.message || result?.error || financeText("Updated return invoice PDF could not be uploaded."),
            );
        }

        if (!result?.filePath) {
            throw new Error(financeText("PDF upload response did not include filePath."));
        }

        return result;
    }, []);

    const items = useMemo(() => normalizeItems(invoice), [invoice]);
    const currency = firstValue(invoice?.currency, invoice?.invoiceCurrency, "EUR");
    // invoice.totalAmount is the document's own VAT-INCLUSIVE grand total
    // (same field/semantics as any invoice: subtotal + vat - discount) —
    // it must never be used as the pre-VAT "subtotal", or VAT ends up
    // counted twice in the totals shown below and in the exported PDF.
    const subtotal = useMemo(
        () =>
            toNumber(
                firstValue(
                    invoice?.invoiceSalesAmount,
                    items.reduce((sum, item) => sum + item.amount, 0),
                ),
            ),
        [invoice, items],
    );
    const vatTotal = useMemo(
        () =>
            toNumber(
                firstValue(
                    invoice?.vatAmount,
                    items.reduce((sum, item) => sum + item.vatAmount, 0),
                ),
            ),
        [invoice, items],
    );
    const grandTotal = toNumber(
        firstValue(invoice?.totalAmount, subtotal + vatTotal),
    );

    // The three summary cards at the top are this page's only AGGREGATED
    // figures -- they roll every returned line item up into one number. Show
    // them in the display currency the user picked, falling back to the
    // document's own currency when a rate isn't available yet so a e.g. TRY
    // credit note is never mislabeled as EUR. Everything else on the page
    // (item rows, the document totals block, the exported PDF) intentionally
    // keeps the return invoice's native currency.
    const displayCurrency = String(selectedCurrency || "EUR").toUpperCase();
    const formatSummaryMoney = useCallback(
        (amount) => {
            const converted = convertCurrency(amount, currency, displayCurrency);

            return converted === null
                ? formatMoney(amount, currency)
                : formatMoney(converted, displayCurrency);
        },
        [convertCurrency, currency, displayCurrency],
    );

    const returnStatus = firstValue(invoice?.returnStatus, "Draft");
    const isDraft = returnStatus === "Draft";
    const isApproved = returnStatus === "Approved";
    const isCancelled = returnStatus === "Cancelled";
    const originalInvoiceId = firstValue(
        invoice?.originalInvoiceId?._id,
        invoice?.originalInvoiceId,
    );

    const itemsTable = useMemo(
        () => ({
            columns: [
                { Header: financeText("Item"), accessor: "item", width: "14%" },
                { Header: financeText("Description"), accessor: "description", width: "32%" },
                { Header: financeText("Quantity"), accessor: "quantity", align: "right" },
                { Header: financeText("Unit Price"), accessor: "unitPrice", align: "right" },
                { Header: financeText("VAT"), accessor: "vat", align: "right" },
                { Header: financeText("Total"), accessor: "total", align: "right" },
            ],
            rows: items.map((item) => ({
                item: item.code,
                description: item.description,
                quantity: item.quantity,
                unitPrice: formatMoney(item.unitPrice, currency),
                vat: `${item.vatRate.toFixed(2)}%`,
                total: formatMoney(item.totalWithVat, currency),
            })),
        }),
        [currency, items],
    );

    const updateStatus = async () => {
        if (!confirmAction || updatingStatus) return;

        const requestedStatus = confirmAction;
        const pdfWindow =
            requestedStatus === "Approved" ? window.open("", "_blank") : null;

        if (pdfWindow) {
            pdfWindow.document.write(
                "<title>Return Invoice PDF</title><p style='font-family:Arial,sans-serif;padding:24px'>Preparing the updated return invoice PDF...</p>",
            );
            pdfWindow.document.close();
        }

        setUpdatingStatus(true);
        try {
            let pdfUrl = invoice?.pdfUrl || "";
            let emailNotSent = "";

            if (requestedStatus === "Approved") {
                const pdfResult = await uploadReturnInvoicePdf(invoice);
                pdfUrl = pdfResult.filePath;
                // The PDF is saved even when the customer e-mail fails; say so instead of
                // reporting a plain success.
                if (pdfResult.emailSent === false) {
                    emailNotSent = pdfResult.emailError || "e-mail service error";
                }
            }

            const payload = await fetchWithAuth(`/finance/return-invoices/${id}/status`, {
                method: "PATCH",
                body: JSON.stringify({
                    returnStatus: requestedStatus,
                    pdfUrl,
                }),
            });

            let updatedInvoice = unwrapData(payload);

            // Some status endpoints return only a success message. In that case,
            // fetch the updated record so the newly generated PDF URL is used.
            if (!updatedInvoice || typeof updatedInvoice !== "object" || !updatedInvoice._id) {
                const refreshedPayload = await fetchWithAuth(
                    `/finance/return-invoices/${id}`,
                );
                updatedInvoice = unwrapData(refreshedPayload);
            }

            if (!updatedInvoice || typeof updatedInvoice !== "object") {
                throw new Error("The updated return invoice could not be loaded.");
            }

            setInvoice(updatedInvoice);
            const successMessages = {
                Approved: financeText("Return invoice approved successfully."),
                Issued: financeText("Return invoice issued successfully."),
                Cancelled: financeText("Return invoice cancelled successfully."),
            };
            if (emailNotSent) {
                showSnackbar?.(
                    `${successMessages[requestedStatus]} ${financeText("The e-mail to the customer could not be sent")} (${emailNotSent}).`,
                    "warning",
                );
            } else {
                showSnackbar?.(successMessages[requestedStatus], "success");
            }
            setConfirmAction(null);

            if (requestedStatus === "Approved") {
                const updatedPdfUrl = resolvePdfUrl(updatedInvoice.pdfUrl);

                if (!updatedPdfUrl) {
                    pdfWindow?.close();
                    showSnackbar?.(
                        financeText("Return invoice was approved, but the updated PDF URL was not returned."),
                        "warning",
                    );
                    return;
                }

                if (pdfWindow) {
                    pdfWindow.location.replace(updatedPdfUrl);
                } else {
                    window.open(updatedPdfUrl, "_blank", "noopener,noreferrer");
                }
            }
        } catch (requestError) {
            pdfWindow?.close();
            showSnackbar?.(requestError.message || financeText("Status could not be updated."), "error");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const openOriginalInvoice = () => {
        if (!originalInvoiceId) return;
        navigate(`/invoice/finance/jobs/${originalInvoiceId}`, {
            state: {
                invoiceId: originalInvoiceId,
                fromReturnInvoice: true,
            },
        });
    };

    const handleSendToEdm = async () => {
        if (!id || sendingEdm) return;

        if (invoice?.edm?.status === "Sent") {
            showSnackbar?.(financeText("This return invoice was already sent to EDM."), "warning");
            return;
        }

        setSendingEdm(true);
        try {
            const payload = await fetchWithAuth(`/edm/invoices/${id}/send`, {
                method: "POST",
            });

            showSnackbar?.(
                `${financeText("Return invoice sent to EDM successfully")} (${payload.profile}).`,
                "success",
            );

            const refreshedPayload = await fetchWithAuth(`/finance/return-invoices/${id}`);
            const refreshedInvoice = unwrapData(refreshedPayload);
            if (refreshedInvoice) setInvoice(refreshedInvoice);
        } catch (requestError) {
            showSnackbar?.(
                requestError.message || financeText("Return invoice could not be sent to EDM."),
                "error",
            );
        } finally {
            setSendingEdm(false);
        }
    };

    // A successful send only confirms EDM's gateway accepted the message —
    // GİB can still reject it afterward. This re-queries EDM for that
    // actual downstream status instead of leaving "Sent" looking final.
    const handleCheckGibStatus = async () => {
        if (!id || checkingGibStatus) return;

        setCheckingGibStatus(true);
        try {
            const payload = await fetchWithAuth(`/edm/invoices/${id}/status`);

            showSnackbar?.(
                `${financeText("GİB status")}: ${payload.status || financeText("Unknown")}${
                    payload.statusDescription ? ` — ${payload.statusDescription}` : ""
                }`,
                "warning",
            );

            const refreshedPayload = await fetchWithAuth(`/finance/return-invoices/${id}`);
            const refreshedInvoice = unwrapData(refreshedPayload);
            if (refreshedInvoice) setInvoice(refreshedInvoice);
        } catch (requestError) {
            showSnackbar?.(
                requestError.message || financeText("GİB status could not be checked."),
                "error",
            );
        } finally {
            setCheckingGibStatus(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox minHeight="65vh" display="flex" alignItems="center" justifyContent="center">
                    <Stack alignItems="center" spacing={1.5}>
                        <CircularProgress size={34} />
                        <MDTypography variant="button" color="text">
                            {financeText("Loading return invoice...")}
                        </MDTypography>
                    </Stack>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox py={3}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                    spacing={2}
                    mb={3}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Tooltip title={financeText("Back")}>
                            <IconButton
                                onClick={() => navigate(-1)}
                                sx={{ border: "1px solid #e5e7eb" }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <MDTypography variant="h4">{financeText("Return Invoice Detail")}</MDTypography>
                                {invoice && (
                                    <FinanceStatusChip status={returnStatus} />
                                )}
                            </Stack>
                            <MDTypography variant="button" color="text">
                                {financeText("Review returned items, totals and document status.")}
                            </MDTypography>
                        </Box>
                    </Stack>

                    {invoice && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            {/* Display-only: converts the summary cards above
                                the document. The invoice's own currency (shown
                                in Document Information) is never changed. */}
                            <TextField
                                select
                                size="small"
                                label={financeText("Display Currency")}
                                value={selectedCurrency}
                                onChange={(event) => setSelectedCurrency(event.target.value)}
                                sx={{ minWidth: 150 }}
                            >
                                {CURRENCY_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {originalInvoiceId && (
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={openOriginalInvoice}
                                >
                                    {financeText("Original Invoice")}
                                </MDButton>
                            )}
                            {invoice?.pdfUrl && (
                                <MDButton
                                    component="a"
                                    href={resolvePdfUrl(invoice.pdfUrl)}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                    color="dark"
                                    startIcon={<PictureAsPdfIcon />}
                                >
                                    {financeText("View PDF")}
                                </MDButton>
                            )}
                            {isDraft && (
                                <MDButton
                                    color="info"
                                    startIcon={<CheckCircleOutlineIcon />}
                                    onClick={() => setConfirmAction("Approved")}
                                >
                                    {financeText("Approve")}
                                </MDButton>
                            )}
                            {isApproved && (
                                <MDButton
                                    color="success"
                                    startIcon={<CheckCircleOutlineIcon />}
                                    onClick={() => setConfirmAction("Issued")}
                                >
                                    {financeText("Issue")}
                                </MDButton>
                            )}
                            {!isCancelled && (
                                <MDButton
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CancelOutlinedIcon />}
                                    onClick={() => setConfirmAction("Cancelled")}
                                >
                                    {financeText("Cancel")}
                                </MDButton>
                            )}
                            {returnStatus === "Issued" && (
                                <MDButton
                                    variant={invoice?.edm?.status === "Sent" ? "outlined" : "gradient"}
                                    color={invoice?.edm?.status === "Sent" ? "success" : "info"}
                                    startIcon={
                                        invoice?.edm?.status === "Sent" ? (
                                            <CloudDoneIcon />
                                        ) : (
                                            <SendIcon />
                                        )
                                    }
                                    disabled={sendingEdm || invoice?.edm?.status === "Sent"}
                                    onClick={handleSendToEdm}
                                >
                                    {invoice?.edm?.status === "Sent"
                                        ? `${financeText("Sent to EDM")} (${invoice.edm.profile})`
                                        : sendingEdm
                                            ? financeText("Sending...")
                                            : financeText("Send to EDM")}
                                </MDButton>
                            )}
                            {invoice?.edm?.status === "Sent" && (
                                <Tooltip
                                    title={
                                        invoice?.edm?.gibStatus
                                            ? `${financeText("Last checked GİB status")}: ${invoice.edm.gibStatus}${
                                                invoice.edm.gibStatusDescription ? ` — ${invoice.edm.gibStatusDescription}` : ""
                                            }`
                                            : financeText("GİB has not been checked yet — a successful send only confirms EDM received it.")
                                    }
                                    arrow
                                >
                                    <span>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            startIcon={<RefreshIcon />}
                                            disabled={checkingGibStatus}
                                            onClick={handleCheckGibStatus}
                                        >
                                            {checkingGibStatus
                                                ? financeText("Checking...")
                                                : financeText("Check GİB Status")}
                                        </MDButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>
                    )}
                </Stack>

                {error && (
                    <Alert
                        severity="error"
                        action={
                            <MDButton color="error" size="small" onClick={loadInvoice}>
                                {financeText("Retry")}
                            </MDButton>
                        }
                        sx={{ mb: 3 }}
                    >
                        {error}
                    </Alert>
                )}

                {invoice && (
                    <>
                        {isCancelled && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                {financeText("This return invoice is cancelled and no longer affects the original invoice balance.")}
                            </Alert>
                        )}

                        <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <SummaryCard
                                    label={financeText("Return Subtotal")}
                                    value={formatSummaryMoney(subtotal)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <SummaryCard
                                    label={financeText("VAT Total")}
                                    value={formatSummaryMoney(vatTotal)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <SummaryCard
                                    label={financeText("Return Total")}
                                    value={formatSummaryMoney(grandTotal)}
                                    color="#047857"
                                    background="#ecfdf5"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <SummaryCard
                                    label={financeText("Return Type")}
                                    value={
                                        invoice.returnType === "full"
                                            ? financeText("Full Return")
                                            : financeText("Partial Return")
                                    }
                                    color="#1d4ed8"
                                    background="#eff6ff"
                                />
                            </Grid>
                        </Grid>

                        <Paper
                            elevation={0}
                            sx={{ border: "1px solid #e5e7eb", borderRadius: 3, mb: 3 }}
                        >
                            <Box p={2.5}>
                                <Stack direction="row" spacing={1} alignItems="center" mb={2.5}>
                                    <ReceiptLongIcon color="primary" />
                                    <MDTypography variant="h6">{financeText("Document Information")}</MDTypography>
                                </Stack>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Return Invoice No.")}
                                            value={firstValue(
                                                invoice.returnInvoiceNumber,
                                                invoice.invoiceNumber,
                                                invoice.invoiceNo,
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Original Invoice No.")}
                                            value={firstValue(
                                                invoice.originalInvoiceNumber,
                                                invoice.originalInvoiceNo,
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Invoice Date")}
                                            value={formatDate(invoice.invoiceDate || invoice.createdAt)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Issued At")}
                                            value={formatDate(invoice.issuedAt)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Customer")}
                                            value={firstValue(
                                                invoice.customerName,
                                                invoice.customer?.companyName,
                                                invoice.companyName,
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Currency")}
                                            value={String(currency).toUpperCase()}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Created By")}
                                            value={invoice.createdBy}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={3}>
                                        <DetailField
                                            label={financeText("Updated At")}
                                            value={formatDate(invoice.updatedAt)}
                                        />
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 2.5 }} />
                                <DetailField
                                    label={financeText("Return Reason")}
                                    value={invoice.returnReason || financeText("No return reason provided.")}
                                />
                            </Box>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{ border: "1px solid #e5e7eb", borderRadius: 3, overflow: "hidden" }}
                        >
                            <Box p={2.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <DescriptionOutlinedIcon color="primary" />
                                    <Box>
                                        <MDTypography variant="h6">{financeText("Returned Items")}</MDTypography>
                                        <MDTypography variant="caption" color="text">
                                            {financeText("Prices and VAT are inherited from the original invoice.")}
                                        </MDTypography>
                                    </Box>
                                </Stack>
                            </Box>
                            {items.length > 0 ? (
                                <DataTable
                                    table={itemsTable}
                                    entriesPerPage={false}
                                    showTotalEntries={false}
                                    isSorted={false}
                                    noEndBorder
                                />
                            ) : (
                                <MDBox py={4} textAlign="center">
                                    <MDTypography variant="button" color="text">
                                        {financeText("No returned item found.")}
                                    </MDTypography>
                                </MDBox>
                            )}
                            <Box
                                p={2.5}
                                display="flex"
                                justifyContent="flex-end"
                                sx={{ background: "#f8fafc", borderTop: "1px solid #e5e7eb" }}
                            >
                                <Stack spacing={0.8} minWidth={{ xs: "100%", sm: 320 }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <MDTypography variant="button" color="text">
                                            {financeText("Subtotal")}
                                        </MDTypography>
                                        <MDTypography variant="button" fontWeight="medium">
                                            {formatMoney(subtotal, currency)}
                                        </MDTypography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <MDTypography variant="button" color="text">
                                            {financeText("VAT")}
                                        </MDTypography>
                                        <MDTypography variant="button" fontWeight="medium">
                                            {formatMoney(vatTotal, currency)}
                                        </MDTypography>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" justifyContent="space-between">
                                        <MDTypography variant="subtitle2" fontWeight="bold">
                                            {financeText("Return Total")}
                                        </MDTypography>
                                        <MDTypography variant="subtitle2" fontWeight="bold">
                                            {formatMoney(grandTotal, currency)}
                                        </MDTypography>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Paper>
                    </>
                )}
            </MDBox>
            <Footer />

            <Dialog open={Boolean(confirmAction)} onClose={() => setConfirmAction(null)}>
                <DialogTitle>
                    {confirmAction === "Approved"
                        ? financeText("Approve return invoice?")
                        : confirmAction === "Issued"
                            ? financeText("Issue return invoice?")
                            : financeText("Cancel return invoice?")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmAction === "Approved"
                            ? financeText("The return will be validated and made ready for issuing.")
                            : confirmAction === "Issued"
                                ? financeText("The return will be applied to the original invoice balance after it is issued.")
                                : financeText("The return will stop affecting the original invoice balance after cancellation.")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MDButton
                        variant="text"
                        color="secondary"
                        disabled={updatingStatus}
                        onClick={() => setConfirmAction(null)}
                    >
                        {financeText("Keep Current Status")}
                    </MDButton>
                    <MDButton
                        color={
                            confirmAction === "Issued"
                                ? "success"
                                : confirmAction === "Approved"
                                    ? "info"
                                    : "error"
                        }
                        disabled={updatingStatus}
                        onClick={updateStatus}
                    >
                        {updatingStatus ? financeText("Updating...") : financeText("Confirm")}
                    </MDButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default ReturnInvoiceDetail;